/**
 * Widget Index Mapping Utilities
 * -------------------------------------
 * Maps a widget's visible position (filtered UI list) to its actual index
 * in the Redux store's full widget list.
 *
 * Used for add, delete, and update operations that require
 * mapping between UI and data layers.
 */

/**
 * Gets the widget at visibleIndex and finds its exact index in the Redux store widgets array
 * @param visibleIndex - Index in the displayed widgets array (UI layer)
 * @param visibleWidgets - The filtered widgets currently visible in the UI (no footers)
 * @param storeWidgets - The full widgets array from the Redux store
 * @returns The matching index in the Redux store, or -1 if not found
 *
 * @example
 * const storeIndex = getWidgetIndexForOperation(1, visibleWidgets, storeWidgets);
 * if (storeIndex !== -1) dispatch(deleteWidget(storeIndex));
 */
export function getWidgetIndexForOperation(
  visibleIndex: number,
  visibleWidgets: any[],
  storeWidgets: any[]
): number {
  const logPrefix = '[🧩 getWidgetIndexForOperation]';

  console.log(`${logPrefix} 🔍 visibleIndex:`, visibleIndex);
  console.log(`${logPrefix} 📦 visibleWidgets:`, visibleWidgets);
  console.log(`${logPrefix} 📦 storeWidgets:`, storeWidgets);

  // --- Step 1: Validate input parameters
  if (!Array.isArray(visibleWidgets) || !Array.isArray(storeWidgets)) {
    console.warn(`${logPrefix} ⚠️ Invalid parameters — expected both arrays.`);
    return -1;
  }

  if (visibleIndex < 0 || visibleIndex >= visibleWidgets.length) {
    console.warn(`${logPrefix} ⚠️ Visible index out of range: ${visibleIndex}`);
    return -1;
  }

  // --- Step 2: Get the target widget from visibleWidgets at the specified index
  const targetWidget = visibleWidgets[visibleIndex];
  console.log(`${logPrefix} 📍 Step 1: Target widget at visible index ${visibleIndex}:`, targetWidget);

  if (!targetWidget?.type) {
    console.warn(`${logPrefix} ⚠️ Widget missing or has no type at index: ${visibleIndex}`);
    return -1;
  }

  console.log(`${logPrefix} 🏷️ Step 2: Target widget type: "${targetWidget.type}"`);

  // --- Step 3: Find the exact widget object in the storeWidgets array
  console.log(`${logPrefix} 🔎 Step 3: Finding exact widget in store array...`);
  const storeIndex = storeWidgets.findIndex((widget: any) => widget === targetWidget);
  console.log(`${logPrefix} ✅ Step 4: Store index found: ${storeIndex}`);

  if (storeIndex === -1) {
    console.warn(`${logPrefix} ⚠️ Widget not found in storeWidgets.`);
  }

  // --- Step 4: Return the store index
  console.log(`${logPrefix} 🎯 Returning store index: ${storeIndex}`);
  return storeIndex;
}
